import { FormFieldDto } from '../../general/dtos';

export interface FieldDisplayName {
    text: string;
    context?: { before: string; blank: string; after: string };
}

/** Derive display names without changing saved labels or exposing answers. */
export function fieldDisplayNames(fields: FormFieldDto[], html = ''): Record<string, FieldDisplayName> {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const inlineFields = fields.filter(field => field.type === 'inline-text');
    const orderedKeys = Array.from(doc.querySelectorAll('form-field')).map(node => node.getAttribute('key')!)
        .filter(key => inlineFields.some(field => field.key === key));
    inlineFields.forEach(field => { if (!orderedKeys.includes(field.key)) orderedKeys.push(field.key); });
    const numbers = new Map(Array.from(new Set(orderedKeys)).map((key, index) => [key, index + 1]));
    const names: Record<string, FieldDisplayName> = {};
    fields.forEach(field => {
        const label = field.label?.trim();
        if (label || field.type !== 'inline-text') {
            names[field.key] = { text: label ?? '' };
            return;
        }
        const blank = `Blank ${numbers.get(field.key)}`;
        const node = Array.from(doc.querySelectorAll('form-field')).find(node => node.getAttribute('key') === field.key);
        if (!node) { names[field.key] = { text: blank }; return; }
        const block = node.closest('p, li, td, th, h1, h2, h3, h4, h5, h6, blockquote, div') ?? doc.body;
        const clone = block.cloneNode(true) as HTMLElement;
        clone.querySelectorAll('script, style, template, [hidden]').forEach(node => node.remove());
        clone.querySelectorAll('br').forEach(node => node.replaceWith(doc.createTextNode(' ')));
        const marker = '\u0000TARGET\u0000';
        clone.querySelectorAll('form-field, quiz-target').forEach(node => {
            const key = node.getAttribute('key') ?? node.getAttribute('field-key');
            node.replaceWith(doc.createTextNode(key === field.key ? marker : numbers.has(key!) ? '___' : '[field]'));
        });
        const text = clone.textContent?.replace(/\s+/g, ' ').trim() ?? '';
        const position = text.indexOf(marker);
        if (position < 0 || !text.replace(marker, '').replace(/_{3}|\[field\]/g, '').trim()) {
            names[field.key] = { text: blank }; return;
        }
        const beforeText = text.slice(0, position);
        const afterText = text.slice(position + marker.length);
        const before = beforeText.length > 80 ? '…' + beforeText.slice(-80).replace(/^\S*\s/, '') : beforeText;
        const after = afterText.length > 80 ? afterText.slice(0, 80).replace(/\s\S*$/, '') + '…' : afterText;
        const context = { before, blank: `[${blank}]`, after };
        names[field.key] = { text: before + context.blank + after, context };
    });
    return names;
}
